import { useEffect, useState } from "react";
import { Link, Navigate, useSearchParams } from "react-router-dom";
import { MyHorses } from "@/components/MyHorses";
import { Followups } from "@/components/Followups";
import { LogOut, Trash2 } from "lucide-react";
import { toast } from "sonner";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { useAuth } from "@/context/AuthContext";
import { api, errMsg, eur, STATUS } from "@/lib/api";

export const StatusBadge = ({ s }) => {
  const cls = { pagado: "bg-[#2563EB] text-white", enviado: "bg-[#1E3A8A] text-white", entregado: "bg-[#0B1D3A] text-white", fallido: "bg-[#DC2626] text-white", cancelado: "bg-slate-200 text-slate-600", pendiente_pago: "bg-[#EFF6FF] text-[#1E3A8A]" }[s];
  return <span className={`text-xs font-bold px-2.5 py-1 rounded-full ${cls}`} data-testid={`status-badge-${s}`}>{STATUS[s]}</span>;
};

const Orders = () => {
  const [orders, setOrders] = useState(null);
  useEffect(() => { api.get("/orders").then((r) => setOrders(r.data)); }, []);
  if (!orders) return <p className="text-slate-500">Cargando…</p>;
  if (!orders.length) return <p className="text-slate-500" data-testid="orders-empty">Aún no tienes pedidos. <Link to="/tienda" className="text-[#DC2626] font-bold">Ver productos</Link></p>;
  return (
    <div className="space-y-4">
      {orders.map((o) => (
        <div key={o.id} className="border border-slate-200 rounded p-5" data-testid={`order-${o.id}`}>
          <div className="flex flex-wrap justify-between gap-3 items-center">
            <div><p className="font-display font-bold text-xl text-[#0B1D3A]">Pedido {o.number}</p><p className="text-xs text-slate-500">{new Date(o.created_at).toLocaleString("es-ES")}</p></div>
            <div className="flex items-center gap-3"><StatusBadge s={o.status} /><span className="font-bold text-[#DC2626]">{eur(o.total)}</span></div>
          </div>
          <ul className="mt-3 text-sm text-slate-600 space-y-0.5">{o.items.map((i) => <li key={i.slug + i.format_id}>{i.name} · {i.format_label} × {i.qty}</li>)}</ul>
          {["pendiente_pago", "fallido"].includes(o.status) && <Link to={`/pedido/resultado?order=${o.id}&status=ko`} className="text-sm font-bold text-[#DC2626] mt-3 inline-block">Completar pago →</Link>}
        </div>
      ))}
    </div>
  );
};

const AddressForm = () => {
  const { user, setUser } = useAuth();
  const [a, setA] = useState(user.address || { name: user.name, address: "", city: "", postal_code: "", province: "", phone: "" });
  const save = async (e) => {
    e.preventDefault();
    try { const { data } = await api.put("/auth/address", a); setUser({ ...user, address: data.address }); toast.success("Dirección guardada"); }
    catch (e2) { toast.error(errMsg(e2)); }
  };
  return (
    <form onSubmit={save} className="grid sm:grid-cols-2 gap-4 max-w-2xl">
      {[["name", "Nombre"], ["phone", "Teléfono"], ["address", "Dirección"], ["postal_code", "Código postal"], ["city", "Población"], ["province", "Provincia"]].map(([k, l]) => (
        <div key={k}><Label>{l}</Label><Input required value={a[k] || ""} onChange={(e) => setA({ ...a, [k]: e.target.value })} data-testid={`address-${k}-input`} className="mt-1.5 h-11" /></div>
      ))}
      <button data-testid="address-save-button" className="sm:col-span-2 justify-self-start bg-[#DC2626] hover:bg-[#B91C1C] text-white font-display font-bold px-6 py-3 rounded-full">Guardar dirección</button>
    </form>
  );
};

const Diets = () => {
  const [list, setList] = useState(null);
  const load = () => api.get("/diet/saved").then((r) => setList(r.data));
  useEffect(() => { load(); }, []);
  if (!list) return <p className="text-slate-500">Cargando…</p>;
  if (!list.length) return <p className="text-slate-500" data-testid="diets-empty">No has guardado ninguna dieta. <Link to="/calculadora" className="text-[#DC2626] font-bold">Usar la calculadora</Link></p>;
  return (
    <div className="grid sm:grid-cols-2 gap-4">
      {list.map((d) => (
        <div key={d.id} className="border border-slate-200 rounded p-5" data-testid={`saved-diet-${d.id}`}>
          <div className="flex justify-between">
            <p className="font-display font-bold text-xl text-[#0B1D3A]">{d.input.horse_name || "Mi caballo"} · {d.result.weight_kg} kg</p>
            <button onClick={async () => { await api.delete(`/diet/saved/${d.id}`); load(); }} className="text-slate-400 hover:text-[#DC2626]" data-testid={`delete-diet-${d.id}`}><Trash2 size={16} /></button>
          </div>
          <p className="text-xs text-slate-500">{d.result.activity_label} · {new Date(d.created_at).toLocaleDateString("es-ES")}</p>
          <p className="text-sm mt-3">Forraje <b>{d.result.forage_kg} kg</b> · Pienso <b>{d.result.concentrate_kg} kg</b> · Agua <b>{d.result.water_l} L</b> · Sal <b>{d.result.salt_g} g</b></p>
          <p className="text-sm text-slate-600 mt-2">{d.result.supplements.map((s) => s.name).join(", ")}</p>
        </div>
      ))}
    </div>
  );
};

export default function Account() {
  const { user, logout } = useAuth();
  const [params] = useSearchParams();
  if (user === null) return <div className="py-32 text-center text-slate-500">Cargando…</div>;
  if (user === false) return <Navigate to="/login?next=/cuenta" replace />;
  return (
    <div className="max-w-5xl mx-auto px-4 py-12" data-testid="account-page">
      <div className="flex flex-wrap justify-between items-end gap-4">
        <div><p className="text-[#DC2626] font-bold text-sm tracking-[0.2em] uppercase">Mi cuenta</p><h1 className="font-display font-black text-4xl sm:text-5xl text-[#0B1D3A] mt-2">Hola, {user.name}</h1></div>
        <div className="flex gap-2">
          {user.role === "admin" && <Link to="/admin" className="bg-[#0B1D3A] text-white font-display font-bold px-5 py-2.5 rounded-full" data-testid="account-admin-link">Panel admin</Link>}
          <button onClick={logout} data-testid="logout-button" className="flex items-center gap-2 border-2 border-[#0B1D3A] text-[#0B1D3A] font-display font-bold px-5 py-2 rounded-full"><LogOut size={16} /> Salir</button>
        </div>
      </div>
      <Tabs defaultValue={params.get("tab") || "orders"} className="mt-10">
        <TabsList className="bg-[#EFF6FF] max-w-full overflow-x-auto justify-start">
          <TabsTrigger value="orders" data-testid="account-tab-orders">Pedidos</TabsTrigger>
          <TabsTrigger value="address" data-testid="account-tab-address">Dirección</TabsTrigger>
          <TabsTrigger value="diets" data-testid="account-tab-diets">Mis dietas</TabsTrigger>
          <TabsTrigger value="caballos" data-testid="account-tab-horses">Mis caballos</TabsTrigger>
          <TabsTrigger value="seguimiento" data-testid="account-tab-followups">Seguimiento</TabsTrigger>
        </TabsList>
        <TabsContent value="orders" className="pt-6"><Orders /></TabsContent>
        <TabsContent value="address" className="pt-6"><AddressForm /></TabsContent>
        <TabsContent value="diets" className="pt-6"><Diets /></TabsContent>
        <TabsContent value="caballos" className="pt-6"><MyHorses /></TabsContent>
        <TabsContent value="seguimiento" className="pt-6"><Followups /></TabsContent>
      </Tabs>
      {user.role !== "admin" && (
        <button onClick={async () => {
          if (!window.confirm("¿Borrar tu cuenta y todos tus datos (caballos, reservas, avisos)? Esta acción no se puede deshacer.")) return;
          await api.delete("/auth/me"); logout(); window.location.href = "/";
        }} className="mt-16 text-sm font-bold text-slate-500 hover:text-[#DC2626] underline" data-testid="delete-account-button">Borrar mi cuenta y mis datos</button>
      )}
    </div>
  );
}
